/**
 * Три поля Нової Пошти в оформленні: спосіб отримання, пункт, точка видачі.
 *
 * Розмітку й конфіг друкує PHP (app/Integrations/nova-poshta-directory.php),
 * тут — лише поведінка: пошук пункту на сервері, довантаження точок видачі
 * під обраний пункт і тип, скидання залежних полів.
 *
 * ── Чому tom-select ініціалізується тут, а не в main.js ──────────────────
 *
 * main.js піднімає 'select.tom-select:not(.tomselected)' один раз на
 * DOMContentLoaded і більше до них не повертається. Двом із трьох наших
 * селектів цього мало: вони наповнюються асинхронно, а населений пункт
 * шукається взагалі на сервері (їх десятки тисяч, у сторінку не покладеш).
 * Тому ці три беремо на себе повністю — і щоб не сваритися з main.js,
 * ініціалізуємо їх ДО нього, поки класу '.tomselected' на них немає.
 *
 * ── Порядок залежностей ─────────────────────────────────────────────────
 *
 *   тип     -> міняє підпис третього поля й перезавантажує його список;
 *   пункт   -> завантажує точки видачі, скидає обрану;
 *   точка   -> нічого не тягне, це кінець ланцюжка.
 *
 * Поля стоять ЗА межами #order_review, тож перемальовування підсумків їх не
 * чіпає: ініціалізація одноразова, слухачі не переприв'язуються.
 */

import TomSelect from 'tom-select';

const readConfig = (root) => {
  const holder = root.querySelector('[data-np-config]');

  if (!holder) return null;

  try {
    return JSON.parse(holder.textContent);
  } catch {
    // Зіпсований JSON — помилка рендера, а не стан сторінки. Без конфігу
    // поля лишаються звичайними select'ами: порожніми, але не зламаними.
    return null;
  }
};

/** Запит до довідника. Порожній масив — і при мережевій, і при серверній помилці. */
const fetchDirectory = async (config, action, params) => {
  const url = new URL(config.ajaxUrl, window.location.origin);

  url.searchParams.set('action', action);
  url.searchParams.set('nonce', config.nonce);

  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));

  try {
    const response = await fetch(url, { credentials: 'same-origin' });
    const body = await response.json();

    return Array.isArray(body?.data) ? body.data : [];
  } catch {
    return [];
  }
};

const baseOptions = (select) => ({
  valueField: 'ref',
  labelField: 'name',
  searchField: ['name'],
  create: false,
  maxOptions: null,
  placeholder: select.getAttribute('placeholder') || '',

  // Вибір має завершуватись одним кліком: список закривається, набраний
  // запит зникає, у полі лишається сама назва. Без цього поруч з обраним
  // пунктом висів недостертий запит, назва через це обрізалась, а список
  // лишався відкритим — вибір виглядав незавершеним.
  closeAfterSelect: true,

  onItemAdd() {
    this.setTextboxValue('');
    // Скидаємо фільтр списку, інакше наступне відкриття покаже результати
    // попереднього запиту, а не всі доступні варіанти.
    this.refreshOptions(false);
  },

  render: {
    no_results: () => `<div class="no-results">${select.dataset.noresults || ''}</div>`,
  },
});

/**
 * Поле з пошуком очищається на час набору.
 *
 * Поки список відкритий, верстка віддає всю ширину полю вводу, і обране
 * значення поруч із ним лишається обрізаним (правило '.ts-control .item +
 * input' у main.scss). Тому на цей час обране ховається зовсім — робить це
 * CSS за класом, який вішаємо тут.
 *
 * Ховається саме показ, не значення: закривши список нічого не обравши,
 * користувач бачить свій попередній вибір на місці.
 *
 * Підказки з обраним значенням тут немає навмисно: tom-select сам керує
 * 'placeholder' поля вводу (updatePlaceholder() чистить його, щойно в
 * контролі з'явився елемент) і перезаписує будь-яке наше значення відразу
 * після фокуса. Боротися з цим заради службового тексту не варто.
 */
const markSearchable = (ts) => ts.wrapper.classList.add('is-searchable');

const initFields = (root) => {
  const config = readConfig(root);
  const typeEl = root.querySelector('[data-np-type]');
  const settlementEl = root.querySelector('[data-np-settlement]');
  const warehouseEl = root.querySelector('[data-np-warehouse]');
  const nameEl = root.querySelector('[data-np-settlement-name]');
  const targetEl = root.querySelector('[data-np-target]');

  if (!config || !typeEl || !settlementEl || !warehouseEl) return;

  const type = new TomSelect(typeEl, { ...baseOptions(typeEl), controlInput: null });

  // Пошук на сервері: 'load' викликається на кожен ввід, тому тримаємо
  // мінімальну довжину — коротший запит НП однаково відкидає.
  const settlement = new TomSelect(settlementEl, {
    ...baseOptions(settlementEl),
    shouldLoad: (query) => query.trim().length >= 2,
    load: async (query, callback) => {
      callback(await fetchDirectory(config, 'nmhg_np_settlements', { q: query }));
    },
    render: {
      ...baseOptions(settlementEl).render,
      loading: () => `<div class="loading">${config.i18n.loading}</div>`,
      no_results: (data) =>
        `<div class="no-results">${
          data.input.trim().length >= 2 ? config.i18n.empty : config.i18n.typeToSearch
        }</div>`,
    },
  });

  const warehouse = new TomSelect(warehouseEl, baseOptions(warehouseEl));

  // Тип не шукають — у ньому два варіанти й немає поля вводу.
  [ settlement, warehouse ].forEach(markSearchable);

  // Значення, збережене в сесії: список під нього ще не завантажений, тож
  // відновлюємо після першого ж завантаження.
  let pending = warehouseEl.dataset.selected || '';

  const loadWarehouses = async () => {
    const settlementRef = settlement.getValue();

    warehouse.clear(true);
    warehouse.clearOptions();

    if (!settlementRef) {
      warehouse.settings.placeholder = config.i18n.pickSettlement;
      warehouse.disable();
      warehouse.sync();
      return;
    }

    warehouse.disable();
    warehouse.settings.placeholder = config.i18n.loading;
    warehouse.sync();

    const rows = await fetchDirectory(config, 'nmhg_np_warehouses', {
      settlement: settlementRef,
      type: type.getValue(),
    });

    warehouse.addOptions(rows);
    warehouse.settings.placeholder = rows.length ? config.i18n.typeToSearch : config.i18n.empty;

    if (rows.length) warehouse.enable();

    if (pending && rows.some((row) => row.ref === pending)) {
      warehouse.setValue(pending, true);
    }

    pending = '';
    warehouse.sync();
  };

  // ── Стан помилки ───────────────────────────────────────────────────────
  //
  // Окремих повідомлень під полями немає: помилка одна й вона вгорі форми
  // (nova-poshta-directory.php). Тут — лише те, що показує, ЯКЕ поле
  // порожнє: рядок позначається як невалідний, а клас верстки '.is-require'
  // на нього вішає helpers/checkout.js — той самий шлях, що в решти полів
  // форми.
  const rows = new Map([
    [type, typeEl.closest('.form-row')],
    [settlement, settlementEl.closest('.form-row')],
    [warehouse, warehouseEl.closest('.form-row')],
  ]);

  // Ставимо 'woocommerce-invalid', а НЕ 'is-require' напряму.
  //
  // Клас верстки на рядки вішає helpers/checkout.js — він дзеркалить
  // 'woocommerce-invalid' у 'is-require' для всієї форми. Якщо ставити
  // 'is-require' звідси, його спостерігач побачить зміну класу, не знайде
  // 'woocommerce-invalid' і зніме клас назад тим самим кадром: два хелпери
  // воюють, і поле не підсвічується.
  const markEmpty = () => rows.forEach((row, ts) => row?.classList.toggle('woocommerce-invalid', !ts.getValue()));
  const clearMark = (ts) => rows.get(ts)?.classList.remove('woocommerce-invalid');

  // Невдалий сабміт видно з розмітки: WooCommerce вставляє на початок форми
  // блок .woocommerce-NoticeGroup-checkout. Слухаємо саме це.
  //
  // Не подію 'checkout_error': вона jQuery-івська, а модуль вантажиться
  // Vite'ом — на те, що jQuery вже є в момент реєстрації, покластись не
  // вийшло (перевірено: слухач не з'являвся серед обробників document.body).
  // Спостерігач за розміткою від порядку завантаження скриптів не залежить
  // зовсім, і це той самий підхід, що вже застосований у helpers/checkout.js.
  const form = root.closest('form.woocommerce-checkout');

  // Спостерігач ОБОВ'ЯЗКОВО в змінній: без посилання на нього об'єкт нічим
  // не утримується й може піти під збирач сміття разом зі спостереженням.
  // Тут посилання живе в замиканні initFields, яке тримають обробники
  // 'change' нижче. Те саме зроблено в helpers/checkout.js.
  const noticeObserver = new MutationObserver((records) => {
    const noticeAppeared = records.some((record) =>
      [...record.addedNodes].some(
        (node) => node.nodeType === 1 && node.classList.contains('woocommerce-NoticeGroup'),
      ),
    );

    if (noticeAppeared) markEmpty();
  });

  if (form) noticeObserver.observe(form, { childList: true });

  type.on('change', (value) => {
    clearMark(type);

    // Підпис третього поля йде за типом: «Відділення» / «Поштомат».
    if (targetEl && config.targets[value]) targetEl.textContent = config.targets[value];

    loadWarehouses();
  });

  settlement.on('change', () => {
    clearMark(settlement);

    // Підпис пункту кладемо в приховане поле: у замовленні має лежати
    // назва, а не самий лише Ref.
    if (nameEl) {
      const option = settlement.options[settlement.getValue()];
      nameEl.value = option ? option.name : '';
    }

    loadWarehouses();
  });

  warehouse.on('change', () => clearMark(warehouse));

  // Стартовий стан: якщо пункт уже обраний (повернулись після помилки
  // валідації), одразу тягнемо його точки видачі.
  loadWarehouses();
};

const init = () => document.querySelectorAll('[data-np-fields]').forEach(initFields);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
